// Room type defaults — updated for v6 price code schema (2026-02-11).
//
// Code map (v6):
//   S-03.01 = Muren schilderen standaard (m²) — €16
//   S-03.03 = Plafonds schilderen standaard (m²) — €18   (was S-03.02 in v5)
//   S-04.01 = Binnendeur vlak lakken (stuk) — €190       (was S-03.05 in v5)
//   S-04.04 = Plinten lakken (lm) — €9                    (was S-03.04 in v5)
//   S-04.05 = Raam binnenzijde lakken (stuk) — €165      (was S-03.06 in v5)
//   S-02.01 = Plamuurwerk licht (m²) — €7
//   S-01.01 = Beschermen en afplakken (forfait) — €95
//
// auto_kind drives quantity calculation from L/B/H dimensions:
//   - "walls"   : qty = 2*(L+W)*H  (formula='custom', auto_qty=true)
//   - "ceiling" : qty = L*W        (formula='ceiling', auto_qty=true)
//   - "floor"   : qty = L*W        (formula='floor',   auto_qty=true)
//   - "plinten" : qty = 2*(L+W)    (formula='linear',  auto_qty=true)
//   - "fixed"   : qty = 1          (formula='fixed',   auto_qty=true)
//   - "piece"   : qty = user input (formula='piece',   auto_qty=false, default=0)

// v6 F42k — Fase 3 Zone-vereenvoudiging: nieuwe binnenzone-categorieën
// (Binnenruimte, Kantoor, Magazijn, Industriële ruimte, Publieke ruimte,
// Andere ruimte, Algemeen). Legacy zones (living, bedroom, hall, bathroom,
// kitchen, garage) blijven backwards-compatible: bestaande records blijven
// bewerkbaar, maar zijn geen preset-keuze meer voor NIEUWE zones.
// v6 F42k Correctieronde v6 — Preset heet nu "Woonruimte" (key blijft
// `binnenruimte` voor backwards compat: bestaande rooms met deze key
// blijven werken; naamgeving voor NIEUWE zones = "Woonruimte 1", "Woonruimte 2".
export const ROOM_TYPES = [
  { key: "binnenruimte",  label: "Woonruimte" },
  { key: "kantoor",       label: "Kantoor" },
  { key: "magazijn",      label: "Magazijn" },
  { key: "industrieel",   label: "Industriële ruimte" },
  { key: "publieke_ruimte", label: "Publieke ruimte" },
  { key: "other",         label: "Andere ruimte" },
  { key: "general",       label: "Algemeen" },
];

// v6 F42k — Legacy ROOM_TYPES (voor labels op bestaande kamers). Deze worden
// NIET meer in de preset-menu getoond, maar het label wordt wél gebruikt voor
// zone-header rendering en type-detectie in de calculator.
// v6 F42k Correctieronde v6 — Behoud "Binnenruimte" als legacy-label zodat
// bestaande zones die deze exacte naam kregen bij creatie ongewijzigd blijven.
export const LEGACY_ROOM_TYPES = [
  { key: "binnenruimte_legacy", label: "Binnenruimte" },
  { key: "living",    label: "Living" },
  { key: "bedroom",   label: "Slaapkamer" },
  { key: "hall",      label: "Hal / Inkom" },
  { key: "bathroom",  label: "Badkamer" },
  { key: "kitchen",   label: "Keuken" },
  { key: "garage",    label: "Garage" },
];

// Volledige lookup (nieuw + legacy) — voor UI-labels op bestaande records.
export const ALL_ROOM_TYPES = [...ROOM_TYPES, ...LEGACY_ROOM_TYPES];

const A = {
  walls:   { code: "S-03.01", label: "Muren schilderen",   auto_kind: "walls"   },
  ceiling: { code: "S-03.03", label: "Plafond schilderen", auto_kind: "ceiling" },
  doors:   { code: "S-04.01", label: "Deuren lakken",      auto_kind: "piece"   },
  windows: { code: "S-04.05", label: "Ramen lakken",       auto_kind: "piece"   },
  plinten: { code: "S-04.04", label: "Plinten lakken",     auto_kind: "plinten" },
  plamuur: { code: "S-02.01", label: "Plamuurwerk licht",  auto_kind: "walls"   },
  tape:    { code: "S-01.01", label: "Afplakken",          auto_kind: "fixed"   },
};

const check = (action, default_checked) => ({ ...action, default_checked });

export const ROOM_DEFAULTS = {
  // v6 F34 — Legacy fallback-regels ("Deuren lakken", "Ramen lakken", "Afplakken")
  // volledig verwijderd uit alle kamertypes. Deur- en raamwerken horen nu
  // uitsluitend in Binnenschrijnwerk-zones. Beschermen/afplakken zit forfaitair
  // in het interior-template (S-01.01, default UIT). Deze defaults dienen enkel
  // nog als fallback wanneer géén interior-template actief is op het project.
  living: [
    check(A.walls,   true),
    check(A.ceiling, true),
    check(A.plinten, false),
    check(A.plamuur, false),
  ],
  bedroom: [
    check(A.walls,   true),
    check(A.ceiling, true),
    check(A.plinten, false),
    check(A.plamuur, false),
  ],
  hall: [
    check(A.walls,   true),
    check(A.ceiling, true),
    check(A.plinten, true),
    check(A.plamuur, false),
  ],
  bathroom: [
    check(A.ceiling, true),
    { ...A.walls, label: "Muren schilderen (indien nodig)", default_checked: false },
    check(A.plamuur, false),
  ],
  kitchen: [
    check(A.walls,   true),
    check(A.ceiling, true),
    check(A.plamuur, false),
    check(A.plinten, false),
  ],
  garage: [
    check(A.walls,   true),
    { ...A.ceiling, label: "Plafond schilderen (indien nodig)", default_checked: false },
  ],
  other: [
    check(A.walls,   false),
    check(A.ceiling, false),
    check(A.plinten, false),
  ],
  // v6 F42k Fase 3 — Nieuwe binnenzone-defaults. Zelfde walls/ceiling patroon
  // als "living", aan te vullen door user.
  binnenruimte: [
    check(A.walls,   true),
    check(A.ceiling, true),
    check(A.plinten, false),
    check(A.plamuur, false),
  ],
  kantoor: [
    check(A.walls,   true),
    check(A.ceiling, true),
    check(A.plinten, false),
  ],
  magazijn: [
    check(A.walls,   true),
    { ...A.ceiling, label: "Plafond schilderen (indien nodig)", default_checked: false },
  ],
  industrieel: [
    check(A.walls,   false),
    { ...A.ceiling, label: "Plafond schilderen (indien nodig)", default_checked: false },
  ],
  publieke_ruimte: [
    check(A.walls,   true),
    check(A.ceiling, true),
    check(A.plinten, true),
  ],
  // v6 F9.2 — Trap zone: geen vaste defaults. Werken komen via een
  // trap-template (Trap standaard / renovatie / vernissen / balustrade)
  // of via manueel toevoegen van T-codes. L/B/H zijn niet van toepassing —
  // hoeveelheden komen uit Room.stair_props (treden, leuning lm, ...).
  stair: [],
  // v6 F9.3 Batch C — Algemeen zone: projectbrede kosten (regie, verplaatsing,
  // materiaaltoeslag, hoogtewerker, ...). Geen L/B/H, geen meetmethode, geen
  // wand-aftrekken. Werken komen via het 'Algemene werfkosten'-template of
  // 'Andere code toevoegen' (A-codes).
  general: [],
};

// v6 E2 — sum room-level wall deductions (door / window / fixed cabinet / ...).
// Each deduction = L × H × multiplier (multiplier defaults to 1).
export function roomWallDeductionTotal(room) {
  return (room?.deductions || []).reduce((s, d) => {
    const L = +d.length || 0;
    const H = +d.height || 0;
    const m = Math.max(1, +d.multiplier || 1);
    const a = L * H * m;
    return a > 0 ? s + a : s;
  }, 0);
}

// v6 F7 — Vrije opmeting helpers
export function wallsGrossFromSegments(room) {
  return (room?.wall_segments || []).reduce(
    (s, w) => s + (Math.max(0, +w.length || 0) * Math.max(0, +w.height || 0)),
    0,
  );
}

export function ceilingGrossFromSegments(room) {
  return (room?.ceiling_segments || []).reduce(
    (s, c) => s + (Math.max(0, +c.length || 0) * Math.max(0, +c.width || 0)),
    0,
  );
}

// v6 F10.4 — Vloeroppervlakte = som van plafondsegmenten (zelfde footprint
// in een normale kamer). Een aparte `floor_segments`-structuur bestaat niet
// en wordt voor MVP niet ingevoerd; de gebruiker kan een per-vlak-uitzondering
// nog steeds via "Andere code toevoegen" toevoegen.
export function floorGrossFromSegments(room) {
  return ceilingGrossFromSegments(room);
}

// v6 F10.4 — Plinten-perimeter in vrije opmeting = som van wand-segment-
// lengtes (i.p.v. bounding-box 2(L+W) die L-vormige kamers fout meet).
export function plintenLengthFromSegments(room) {
  return (room?.wall_segments || []).reduce(
    (s, w) => s + Math.max(0, +w.length || 0),
    0,
  );
}

// Build the default 4 walls + 1 ceiling from quick dimensions, used as
// pre-fill when switching quick → free for the first time.
export function buildDefaultSegmentsFromQuick(room) {
  const L = +room?.length || 0;
  const W = +room?.width || 0;
  const H = +room?.height || 0;
  const id = () => (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `seg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  return {
    wall_segments: [
      { id: id(), label: "Muur 1", length: L, height: H },
      { id: id(), label: "Muur 2", length: W, height: H },
      { id: id(), label: "Muur 3", length: L, height: H },
      { id: id(), label: "Muur 4", length: W, height: H },
    ],
    ceiling_segments: [
      { id: id(), label: "Plafond 1", length: L, width: W },
    ],
  };
}

// Compute auto-quantity from room dimensions.
// Wall deductions apply ONLY to "walls" auto_kind. The third arg controls
// whether the room-level deductions are subtracted (v6 E3 — per-pricecode flag
// `apply_wall_deductions` drives this). Other kinds (ceiling, floor, plinten,
// piece, fixed, linear, hour) are NEVER reduced by room-level deductions.
//
// v6 F7 — als room.measure_method === "free" gebruikt walls de som van
// wall_segments (L×H per segment) en ceiling de som van ceiling_segments
// (L×W per segment). Aftrekken blijven werken op de totale bruto.
export function autoQuantity(auto_kind, room, applyDeductions = true) {
  const L = +room?.length || 0;
  const W = +room?.width || 0;
  const H = +room?.height || 0;
  const isFree = room?.measure_method === "free";
  switch (auto_kind) {
    case "walls": {
      const gross = isFree
        ? wallsGrossFromSegments(room)
        : Math.max(0, 2 * (L + W) * H);
      if (!applyDeductions) return gross;
      const dedu = roomWallDeductionTotal(room);
      return Math.max(0, gross - dedu);
    }
    case "ceiling":
      return isFree
        ? ceilingGrossFromSegments(room)
        : Math.max(0, L * W);
    case "floor":
      // v6 F10.4 — Vloer in vrije opmeting = som van ceiling-segments (zelfde
      // footprint). Backend gebruikt dezelfde regel via room.measure_method.
      return isFree
        ? floorGrossFromSegments(room)
        : Math.max(0, L * W);
    case "plinten":
      // v6 F10.4 — Plinten in vrije opmeting = perimeter uit wall-segments
      // (i.p.v. bounding-box 2(L+W)).
      return isFree
        ? plintenLengthFromSegments(room)
        : Math.max(0, 2 * (L + W));
    case "fixed":   return 1;
    case "piece":   return 0;
    case "linear":  return 0;   // v6 F9.1 — gebruiker voert lopende meters in
    case "hour":    return 0;   // v6 F9.1 — gebruiker voert uren in
    case "manual":  return 0;   // v6 F9.1 — gebruiker voert m² in (object-codes)
    default:        return 0;
  }
}

// Map auto_kind → backend formula
export const AUTO_KIND_FORMULA = {
  walls:   "custom",
  ceiling: "ceiling",
  floor:   "floor",
  plinten: "linear",
  linear:  "linear",
  fixed:   "fixed",
  piece:   "piece",
  hour:    "custom",
  manual:  "custom",   // v6 F9.1 — losse m²-objectcodes (T-03.*, T-04.*)
};

// v6 F9.2 — Map a price-code (T-XX) → the matching stair_props source field.
// Returns null when the code is not a trap-code or not yet mapped. Used by
// `autoQuantityForAction` to auto-fill measurement quantities from
// Room.stair_props instead of room dimensions when the room is a trap-zone.
export function stairSourceFor(code) {
  const c = (code || "").toUpperCase();
  // Per-trede (preparation T-01.* + finishing T-02.01..T-02.07)
  if (/^T-01\./.test(c)) return "tread_count";
  if (/^T-02\.(0[1-7])$/.test(c)) return "tread_count";
  // Overloop (T-03.04) — m²
  if (c === "T-03.04") return "landing_m2";
  // Onderzijde trap (T-03.05) — m²
  if (c === "T-03.05") return "underside_m2";
  // Trapleuning (T-04.01 schuren, T-04.02 schilderen/lakken) — lm
  if (c === "T-04.01" || c === "T-04.02") return "handrail_lm";
  // Balustrade (T-04.03 eenvoudig, T-04.04 met spijlen) — lm
  if (c === "T-04.03" || c === "T-04.04") return "balustrade_lm";
  // Spijlen afzonderlijk (T-04.05) — stuks
  if (c === "T-04.05") return "spindle_count";
  return null;
}

// v6 F9.2 — Resolve the auto-quantity for an action, with stair-zone awareness.
// When room.room_type === 'stair' and the action's code maps to a stair_source,
// read the value from room.stair_props instead of computing from L/B/H.
// Falls back to the standard `autoQuantity(action.auto_kind, ...)` for normal
// rooms or codes not mapped to stair_props.
export function autoQuantityForAction(action, room, applyDeductions = true) {
  if (room?.room_type === "stair") {
    const src = stairSourceFor(action?.code);
    if (src) return +(room?.stair_props?.[src]) || 0;
  }
  return autoQuantity(action?.auto_kind, room, applyDeductions);
}
